/**
 * ============================================================================
 * DIALOGS STORE - TRANSLATION PROCESSING
 * ============================================================================
 * 
 * Handles creating and updating TMS strings for translatable dialog content.
 * 
 * Translatable Content:
 * - Dialog field text (dialog lines, questions, end text)
 * - Option text (answer choices)
 * 
 * @module dialogs/utils/translation
 */

import { createString, updateStringsItem } from "../../stringsStore";
import type { CreateDialogData, DialogDataContent } from "../types";

// ============================================================================
// TYPES
// ============================================================================

interface ProcessedDialogResult {
  dialog: CreateDialogData;
  createdStrings: string[];
  updatedStrings: string[];
}

// ============================================================================
// TRANSLATION PROCESSING
// ============================================================================

/**
 * Process dialog for translation - create TMS strings for translatable fields and options.
 * 
 * Creates or updates string records in the TMS for:
 * - Translatable field text
 * - Translatable option text
 * 
 * @param dialog - The dialog data to process
 * @param createStringFn - Function to create new strings
 * @param updateStringFn - Optional function to update existing strings
 * @returns Processed dialog with string_ids and lists of created/updated strings
 * 
 * @internal
 */
export async function processDialogForTranslation(
  dialog: CreateDialogData,
  createStringFn: typeof createString,
  updateStringFn?: typeof updateStringsItem
): Promise<ProcessedDialogResult> {
  const createdStrings: string[] = [];
  const updatedStrings: string[] = [];

  if (!dialog.data?.sections || dialog.data.sections.length === 0) {
    return { dialog, createdStrings, updatedStrings };
  }

  const processedSections = await Promise.all(
    dialog.data.sections.map(async (section) => {
      const processedSection = { ...section };

      // Process fields in the section
      if (section.fields && section.fields.length > 0) {
        const processedFields = await Promise.all(
          section.fields.map(async (field, fieldIndex) => {
            const processedField = { ...field };

            // ================================================================
            // PROCESS FIELD TEXT
            // ================================================================
            if (field.translatable && field.text) {
              if (field.string_id && updateStringFn) {
                // Update existing string
                try {
                  await updateStringFn({
                    id: field.string_id,
                    game_id: dialog.game_id,
                    source_text: field.text,
                    abbrev: `${dialog.dialog_key}_${section.section_identifier}_f${fieldIndex + 1}`,
                  });
                  updatedStrings.push(field.string_id);
                } catch (error) {
                  console.error('[processDialogForTranslation] Failed to update string for field:', error);
                }
              } else {
                // Create new string
                try {
                  const newString = await createStringFn({
                    game_id: dialog.game_id,
                    source_text: field.text,
                    abbrev: `${dialog.dialog_key}_${section.section_identifier}_f${fieldIndex + 1}`,
                    context: `Dialog: ${dialog.name} - Section: ${section.section_identifier} - Field ${fieldIndex + 1}`,
                    tags: dialog.tags || [],
                    origin: 'dialog',
                  });
                  processedField.string_id = newString.id;
                  createdStrings.push(newString.id);
                } catch (error) {
                  console.error('[processDialogForTranslation] Failed to create string for field:', error);
                }
              }
            }

            // ================================================================
            // PROCESS OPTIONS (FOR QUESTION FIELDS)
            // ================================================================
            if (field.options && field.options.length > 0) {
              const processedOptions = await Promise.all(
                field.options.map(async (option, optIndex) => {
                  const processedOption = { ...option };

                  if (option.translatable && option.option_text) {
                    if (option.string_id && updateStringFn) {
                      // Update existing string
                      try {
                        await updateStringFn({
                          id: option.string_id,
                          game_id: dialog.game_id,
                          source_text: option.option_text,
                          abbrev: `${dialog.dialog_key}_${section.section_identifier}_f${fieldIndex + 1}_opt${optIndex + 1}`,
                        });
                        updatedStrings.push(option.string_id);
                      } catch (error) {
                        console.error('[processDialogForTranslation] Failed to update string for option:', error);
                      }
                    } else {
                      // Create new string
                      try {
                        const newString = await createStringFn({
                          game_id: dialog.game_id,
                          source_text: option.option_text,
                          abbrev: `${dialog.dialog_key}_${section.section_identifier}_f${fieldIndex + 1}_opt${optIndex + 1}`,
                          context: `Dialog: ${dialog.name} - Section: ${section.section_identifier} - Field ${fieldIndex + 1} - Option ${optIndex + 1}`,
                          tags: dialog.tags || [],
                          origin: 'dialog',
                        });
                        processedOption.string_id = newString.id;
                        createdStrings.push(newString.id);
                      } catch (error) {
                        console.error('[processDialogForTranslation] Failed to create string for option:', error);
                      }
                    }
                  }

                  return processedOption;
                })
              );

              processedField.options = processedOptions;
            }

            return processedField;
          })
        );

        processedSection.fields = processedFields;
      }

      return processedSection;
    })
  );

  return {
    dialog: { 
      ...dialog, 
      data: { 
        ...dialog.data,
        sections: processedSections 
      } as DialogDataContent
    },
    createdStrings,
    updatedStrings,
  };
}

/**
 * Collect all string IDs from a dialog's translatable fields.
 * Used for deprecating strings when a dialog is deleted.
 * 
 * @param data - The dialog data content
 * @returns Array of string IDs
 * 
 * @internal
 */
export function collectStringIdsFromDialog(data: DialogDataContent | undefined): string[] {
  const stringIds: string[] = [];
  
  if (!data?.sections) return stringIds;
  
  data.sections.forEach(section => {
    if (section.fields) {
      section.fields.forEach(field => {
        // Collect field string_id
        if (field.string_id && field.translatable) {
          stringIds.push(field.string_id);
        }
        // Collect option string_ids
        if (field.options) {
          field.options.forEach(option => {
            if (option.string_id && option.translatable) {
              stringIds.push(option.string_id);
            }
          });
        }
      });
    }
  });
  
  return stringIds;
}
